import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import invariant from 'tiny-invariant';
import { areObjectsEqual } from '../utils';

const useOptimisticHook = <
  THookData extends Record<string, unknown>,
  THookProps extends Array<unknown>,
>(
  useOriginalHook: (...props: THookProps) => THookData,
  props: THookProps,
) => {
  const [optimisticData, setOptimisticData] =
    useState<Partial<THookData> | null>(null);
  const originalHooksData = useOriginalHook(...props);

  const _setOptimisticData = useCallback(
    (optimisticData: Partial<THookData>) => {
      setOptimisticData(optimisticData);
    },
    [setOptimisticData],
  );

  useEffect(() => {
    if (optimisticData == null) {
      return;
    }

    let hasChanged = false;
    const newOptimisticData = { ...optimisticData };

    for (const [key, value] of Object.entries(optimisticData)) {
      const originalValue = originalHooksData[key];

      if (
        originalValue === value ||
        (originalValue != null &&
          typeof originalValue === 'object' &&
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore originalValue is an object
          areObjectsEqual(value, originalValue))
      ) {
        delete newOptimisticData[key];
        hasChanged = true;
      }
    }

    if (hasChanged) {
      if (Object.keys(newOptimisticData).length === 0) {
        setOptimisticData(null);
      } else {
        setOptimisticData(newOptimisticData);
      }
    }
  }, [originalHooksData]);

  const data = useMemo(() => {
    if (optimisticData == null) {
      return originalHooksData;
    }

    return {
      ...originalHooksData,
      ...optimisticData,
    };
  }, [originalHooksData, optimisticData]);

  return {
    data,
    setOptimisticData: _setOptimisticData,
  };
};

/**
 * Create a set of context and hooks that wrap an existing hook and overwrite some of its data,
 * allowing for optimistic UI rendering.
 * The data is automatically reset once the original hook data match the optimistic data.
 *
 * The returned provider needs to placed in the tree, then the data hook can be used in
 * replacement of the original hook, and the setter hook is used to update the optimistic data.
 */
export const createOptimisticHook = <
  THookData extends Record<string, unknown>,
  THookProps extends Array<unknown>,
>(
  displayName: string,
  useOriginalHook: (...props: THookProps) => THookData,
  hookProps: THookProps,
) => {
  const OptimisticDataContext = React.createContext<THookData | null>(null);

  OptimisticDataContext.displayName = `${displayName}DataContext`;

  const useOptimisticData = () => {
    const ctx = useContext(OptimisticDataContext);

    invariant(
      ctx,
      'useOptimisticData must be used within a OptimisticProvider',
    );

    return ctx;
  };

  const OptimisticSetterContext = React.createContext<
    ((optimisticData: Partial<THookData>) => void) | null
  >(null);

  OptimisticSetterContext.displayName = `${displayName}SetterContext`;

  const useOptimisticSetter = () => {
    const ctx = useContext(OptimisticSetterContext);

    invariant(
      ctx,
      'useOptimisticSetter must be used within a OptimisticProvider',
    );

    return ctx;
  };

  const OptimisticProvider = ({ children }) => {
    const optimisticHookValue = useOptimisticHook(useOriginalHook, hookProps);

    return (
      <OptimisticDataContext.Provider value={optimisticHookValue.data}>
        <OptimisticSetterContext.Provider
          value={optimisticHookValue.setOptimisticData}
        >
          {children}
        </OptimisticSetterContext.Provider>
      </OptimisticDataContext.Provider>
    );
  };

  OptimisticProvider.displayName = `${displayName}Provider`;

  return [OptimisticProvider, useOptimisticData, useOptimisticSetter] as const;
};
